import { useState, useEffect } from 'react'
import { apiFetch, ApiError } from '../lib/api'
import { getToken, decodeToken } from '../lib/auth'
import type { TaskQueueItem } from '../lib/tasks'
import { CLOSE_TYPES, MANAGER_RESOLVE_TYPES } from './resolvePaneTaskTypes'
import { HelpDot } from './guidance/Guidance'
import Sentence from './Sentence'
import { articleFor, lawWords, natureWords } from '../lib/sentences/words'

/* Resolve pane — the last Work verbs (IO ledger rows on tasks_frame):
 *   look_through_review  → the YES/NO gate (Bible §4665-66): walk through
 *     the entity, or stop the chain there with a reason. Manager call.
 *     POST /tasks/{id}/resolve {outcome, note}.
 *   verification_signoff / verification_remediation →
 *     one Approve with optional note. Manager call. POST /resolve.
 *   daily_digest → informational; Acknowledge closes.
 *     Officer call. POST /tasks/{id}/close.
 * Officers see manager-only actions disabled with an honest notice —
 * the task is visible (no dead end), the button is not theirs. */

const TERMINAL_REASONS: Array<{ value: string; label: string }> = [
  { value: 'terminal_operating_company', label: 'it is an operating company' },
  { value: 'terminal_target_by_policy', label: 'policy treats it as a target' },
  { value: 'terminal_no_control', label: 'the fund does not control it' },
  { value: 'terminal_depositary_derogation', label: 'depositary derogation applies' },
  { value: 'terminal_other', label: 'other (explain below)' },
]

interface DrawerLite {
  title: string
  subtitle: string | null
  facts: Record<string, string | number | boolean>
}

function errText(e: unknown): string {
  if (e instanceof ApiError) {
    const d = (e.body as { detail?: unknown } | undefined)?.detail
    if (typeof d === 'string') return d
    return `${e.statusText} (${e.status})`
  }
  return e instanceof Error ? e.message : 'request failed'
}

/** One drawer fact as a plain string, or null when absent. */
function factOf(facts: Record<string, string | number | boolean>, key: string): string | null {
  const v = facts[key]
  return v === '' || v === null || v === undefined ? null : String(v)
}

/** The words that fill "under «law» law": "Swiss", or "the law of X". */
function lawSlot(jurisdiction: string | null): string | null {
  const w = lawWords(jurisdiction)
  return w.adjective ?? (w.of ? `the ${w.of}` : null)
}

const APPROVE_COPY: Record<string, { title: string; body: string; button: string }> = {
  verification_signoff: {
    title: 'Sign off the fully verified chain',
    /* Say exactly what is being signed, and no more. The old line — "Every
     * path of this fund's ownership chain is verified" — was read next to a
     * queue headline saying N items still block the record, and an
     * ops-literate reader takes that as a contradiction (demo dry run
     * 2026-07-25, risk R4).
     *
     * Ale ruling 2026-07-26 then moved reconciliations INSIDE the claim: an
     * open break blocks sign-off, so reaching this pane means both halves
     * hold — the papers are complete AND the numbers tie out. (With a break
     * open you would not be reading this; the blocked notice replaces it.)
     * Recommended items are the one thing genuinely outside the claim: they
     * are real work with their own queue rows and never gated it. */
    body:
      "Every path of this fund's ownership chain is verified: all mandatory evidence is " +
      'compiled — on entities, positions, instruments and trades — no chain path is left ' +
      "unverified, and the record's reconciliations tie out. Recommended items stay in " +
      'the queue on their own; your sign-off makes the verified state official.',
    button: 'Sign off',
  },
  verification_remediation: {
    title: 'Acknowledge lost verification',
    body: 'The fund dropped below full verification after sign-off. Acknowledging records that remediation is underway; the underlying gaps carry their own tasks.',
    button: 'Acknowledge',
  },
  // fund_setup RETIRED (Ale ruling 2026-07-24, mig 141): the fund/root-entity
  // conflict rides the possible_match question on the election proposal.
}

export default function ResolvePane({
  task,
  onResolved,
}: {
  task: TaskQueueItem
  onResolved: () => void
}) {
  const role = decodeToken(getToken() ?? '')?.role ?? 'ops_officer'
  const isManager = role === 'manager' || role === 'admin'
  const isLookThrough = task.task_type === 'look_through_review'
  const isClose = CLOSE_TYPES.includes(task.task_type)
  const needsManager = MANAGER_RESOLVE_TYPES.includes(task.task_type) && !isManager

  const [entity, setEntity] = useState<DrawerLite | null>(null)
  const [decision, setDecision] = useState<'walk_through' | 'terminal' | null>(null)
  const [terminalReason, setTerminalReason] = useState(TERMINAL_REASONS[0].value)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  // D-3b honest-verified (Ale ruling 2026-07-24): the sign-off offer is
  // only real when the fund's LIVE rollup says verified — chain paths
  // verified AND every mandatory obligation (all object types including
  // trades) compiled; optional/recommended discounted. A sign-off task can
  // outlive that state (new documents, new trades), so re-check on open.
  // Ale ruling 2026-07-26 adds the third precondition: an open
  // reconciliation break. Same posture read, same refusal — the offer, the
  // pane and the server guard share one predicate so they cannot disagree.
  const isSignoff = task.task_type === 'verification_signoff'
  const [signoffFund, setSignoffFund] = useState<{
    verification_pct?: number
    open_mandatory_count?: number
    unverified_path_count?: number
    open_reconciliation_count?: number
  } | null>(null)
  const [signoffFundLoaded, setSignoffFundLoaded] = useState(false)

  useEffect(() => {
    if (!isLookThrough || !task.fund_id || !task.target_id) return
    apiFetch<DrawerLite>(`/api/funds/${task.fund_id}/objects/entity/${task.target_id}`)
      .then(setEntity)
      .catch(() => {})
  }, [isLookThrough, task.fund_id, task.target_id])

  useEffect(() => {
    if (!isSignoff || !task.fund_id) {
      setSignoffFundLoaded(true)
      return
    }
    apiFetch<{
      verification_pct?: number
      open_mandatory_count?: number
      unverified_path_count?: number
      open_reconciliation_count?: number
    }>(`/api/funds/${task.fund_id}`)
      .then((f) => {
        setSignoffFund(f)
        setSignoffFundLoaded(true)
      })
      .catch(() => setSignoffFundLoaded(true)) // unreachable rollup → don't dead-end the pane
  }, [isSignoff, task.fund_id])

  const openMandatory = signoffFund?.open_mandatory_count ?? 0
  const unverifiedPaths = signoffFund?.unverified_path_count ?? 0
  const openRecons = signoffFund?.open_reconciliation_count ?? 0
  const signoffBlocked =
    isSignoff && signoffFund != null && (openMandatory > 0 || unverifiedPaths > 0 || openRecons > 0)

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      if (isClose) {
        await apiFetch(`/api/tasks/${task.task_id}/close`, { method: 'POST' })
        setDone('Closed. It stays in the audit trail.')
      } else if (isLookThrough) {
        const outcome = decision === 'walk_through' ? 'walk_through' : terminalReason
        await apiFetch(`/api/tasks/${task.task_id}/resolve`, {
          method: 'POST',
          body: JSON.stringify({ outcome, note: note || undefined }),
        })
        setDone(
          decision === 'walk_through'
            ? "Recorded: the chain walks through this entity — its positions join the record's scope."
            : 'Recorded: the chain stops at this entity, with your reason on file.'
        )
      } else {
        await apiFetch(`/api/tasks/${task.task_id}/resolve`, {
          method: 'POST',
          body: JSON.stringify(note ? { note } : {}),
        })
        setDone('Resolved — recorded under your name.')
      }
    } catch (e) {
      setError(errText(e))
    } finally {
      setBusy(false)
    }
  }

  if (done)
    return (
      <div className="vcard" style={{ flex: 1, alignSelf: 'flex-start', padding: 22 }}>
        <div style={{ fontSize: 'var(--text-13)', color: 'var(--verified)', fontWeight: 600 }}>
          ✓ {done}
        </div>
        <button className="vbtn pri" style={{ marginTop: 16 }} onClick={onResolved}>
          Next task →
        </button>
      </div>
    )

  const copy = APPROVE_COPY[task.task_type]

  return (
    <div style={{ flex: 1, display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      {/* context */}
      <div className="vcard" style={{ flex: 1, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--line)' }}>
          <div style={{ fontSize: 'var(--text-13)', fontWeight: 700 }}>
            {isLookThrough ? (
              <Sentence meaning="walk.question" values={{ entity: entity?.title ?? null }} />
            ) : isClose ? (
              task.title
            ) : (
              (copy?.title ?? task.title)
            )}{' '}
            {isLookThrough ? <HelpDot k="deck.walk" /> : null}
            {copy ? <HelpDot k="pane.signoff" /> : null}
          </div>
          <div style={{ fontSize: 'var(--text-12)', color: 'var(--faint)', marginTop: 2 }}>
            {isLookThrough
              ? 'an intermediate entity needs the look-through call: walk through it (its positions count) or stop the chain here'
              : isClose
                ? 'informational — nothing changes on the record when you close it'
                : 'manager decision — recorded permanently'}
          </div>
        </div>
        <div style={{ padding: '14px 18px' }}>
          {error && (
            <div style={{ fontSize: 'var(--text-12)', color: 'var(--gap)', marginBottom: 10 }}>
              {error}
            </div>
          )}
          {isLookThrough &&
            entity &&
            (() => {
              const legalForm = factOf(entity.facts, 'legal form')
              const address = factOf(entity.facts, 'registered address')
              const incorporated = factOf(entity.facts, 'incorporated')
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <Sentence
                    as="p"
                    meaning={legalForm ? 'object.fact.entity' : 'object.fact.entity.noform'}
                    style={{ margin: 0, fontSize: 'var(--text-12)' }}
                    values={{
                      entity: entity.title,
                      article: legalForm ? articleFor(legalForm) : null,
                      legal_form: legalForm,
                      law: lawSlot(factOf(entity.facts, 'jurisdiction')),
                      nature: natureWords(factOf(entity.facts, 'nature')),
                    }}
                  />
                  {(address || incorporated) && (
                    <Sentence
                      as="p"
                      meaning="object.fact.entity.seat"
                      style={{ margin: 0, fontSize: 'var(--text-12)' }}
                      values={{ address, incorporated }}
                    />
                  )}
                </div>
              )
            })()}
          {!isLookThrough && (
            <div
              style={{ fontSize: 'var(--text-12)', color: 'var(--muted)', whiteSpace: 'pre-wrap' }}
            >
              {signoffBlocked
                ? "This fund is not fully verified any more — the sign-off below stays unavailable until every mandatory obligation (including on trades) is compiled, every chain path is verified, and the record's reconciliations tie out."
                : (copy?.body ?? task.description ?? '')}
            </div>
          )}
          {isClose && task.description && (
            <div
              style={{ fontSize: 'var(--text-12)', color: 'var(--muted)', whiteSpace: 'pre-wrap' }}
            >
              {task.description}
            </div>
          )}
        </div>
      </div>

      {/* actions */}
      <div style={{ width: 380, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {needsManager && (
          <div
            className="vcard"
            style={{ padding: '12px 16px', fontSize: 'var(--text-12)', color: 'var(--muted)' }}
          >
            This is a manager decision — visible to you, actionable by a manager.
          </div>
        )}

        {isLookThrough ? (
          <div className="vcard" style={{ padding: 16 }}>
            <label
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'flex-start',
                fontSize: 'var(--text-12)',
              }}
            >
              <input
                type="radio"
                checked={decision === 'walk_through'}
                onChange={() => setDecision('walk_through')}
                disabled={needsManager}
                style={{ marginTop: 2 }}
              />
              <span>
                <b>Yes — walk through.</b>{' '}
                <span style={{ color: 'var(--muted)' }}>
                  The fund controls it; what it holds belongs in the record.
                </span>
              </span>
            </label>
            <label
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'flex-start',
                fontSize: 'var(--text-12)',
                marginTop: 10,
              }}
            >
              <input
                type="radio"
                checked={decision === 'terminal'}
                onChange={() => setDecision('terminal')}
                disabled={needsManager}
                style={{ marginTop: 2 }}
              />
              <span>
                <b>No — the chain stops here.</b>{' '}
                <span style={{ color: 'var(--muted)' }}>
                  The entity stays a target; record why:
                </span>
              </span>
            </label>
            {decision === 'terminal' && (
              <select
                value={terminalReason}
                onChange={(e) => setTerminalReason(e.target.value)}
                style={{
                  marginTop: 8,
                  marginLeft: 24,
                  font: 'inherit',
                  fontSize: 'var(--text-12)',
                  padding: '6px 8px',
                  border: '1px solid var(--line)',
                  borderRadius: 7,
                }}
              >
                {TERMINAL_REASONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            )}
            <input
              placeholder="note (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={needsManager}
              style={{
                width: '100%',
                marginTop: 12,
                font: 'inherit',
                fontSize: 'var(--text-12)',
                padding: '7px 10px',
                border: '1px solid var(--line)',
                borderRadius: 7,
              }}
            />
            <button
              className="vbtn pri"
              style={{ marginTop: 12 }}
              onClick={submit}
              disabled={busy || needsManager || decision === null}
            >
              Record decision
            </button>
          </div>
        ) : (
          <div className="vcard" style={{ padding: 16 }}>
            <div style={{ fontSize: 'var(--text-13)', fontWeight: 700 }}>
              {isClose ? 'Acknowledge' : (copy?.title ?? 'Approve')}
            </div>
            {signoffBlocked && (
              <div
                data-testid="signoff-blocked"
                style={{
                  fontSize: 'var(--text-12)',
                  color: 'var(--gap)',
                  marginTop: 8,
                  fontWeight: 600,
                }}
              >
                {openMandatory > 0 ? (
                  <Sentence meaning="pane.blocked_signoff" values={{ count: openMandatory }} />
                ) : unverifiedPaths > 0 ? (
                  `${unverifiedPaths} chain path${unverifiedPaths === 1 ? '' : 's'} not yet verified — sign-off unavailable.`
                ) : (
                  `${openRecons} record reconciliation${openRecons === 1 ? '' : 's'} still open — the record's arithmetic doesn't tie out; resolve ${openRecons === 1 ? 'it' : 'them'} before signing off.`
                )}
                <div style={{ fontWeight: 400, color: 'var(--muted)', marginTop: 4 }}>
                  {openMandatory === 0 && unverifiedPaths === 0
                    ? /* A break is not a missing document, and no document has been
                         requested for it: until the officer says whether a movement is
                         missing or a balance moved, there is nothing to ask anyone for. */
                      'A balance we hold disagrees with the movements we hold, or a movement has no record. Work the reconciliations in the queue — once you have settled what changed, sign-off returns here.'
                    : 'The record is not fully verified: sign-off returns here once every mandatory obligation — including on trades — is compiled and every chain path is verified.'}
                </div>
              </div>
            )}
            {!isClose && (
              <input
                placeholder="note (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={needsManager || signoffBlocked}
                style={{
                  width: '100%',
                  marginTop: 10,
                  font: 'inherit',
                  fontSize: 'var(--text-12)',
                  padding: '7px 10px',
                  border: '1px solid var(--line)',
                  borderRadius: 7,
                }}
              />
            )}
            <button
              className="vbtn pri"
              style={{ marginTop: 12 }}
              onClick={submit}
              disabled={busy || needsManager || signoffBlocked || (isSignoff && !signoffFundLoaded)}
            >
              {isClose ? 'Acknowledge & close' : (copy?.button ?? 'Approve')}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
